// Spec 47 approval-ux — cmp_approval_impact_callout / impact_callout.
//
// "A one-line high-density summary of the decision's scope: number of
// people affected, currency/percentage delta, and any compliance flag.
// It is the fastest read on a decision card and the only block that
// persists in notification digest emails." (spec 47 line 521)
//
// a11y per spec 47:
//   - "Screen reader reads full line as a single aria-label to avoid
//     choppy announcements"
//   - "Icons carry aria-hidden='true'; their meaning is duplicated in text"
//   - "Color-only flagging is never used — compliance_flag always pairs
//     icon + word + color"

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface ImpactComplianceFlag {
  readonly text: string;
  /** Optional href to policy detail; renders as <a> when set. */
  readonly policyHref?: string;
}

export interface ImpactCalloutProps extends HTMLAttributes<HTMLDivElement> {
  /** "27 people" or "This employee" — required. */
  readonly affectedCount: string;
  /** "+$27,000 base" or "+16.4%" — required. */
  readonly primaryDelta: string;
  /** Optional secondary line: "+$48.2k/yr annualized". */
  readonly secondaryDelta?: string;
  /** Optional compliance/policy flag. */
  readonly complianceFlag?: ImpactComplianceFlag;
  /** Optional decorative icon (renders aria-hidden). */
  readonly icon?: ReactNode;
}

export const ImpactCallout = forwardRef<HTMLDivElement, ImpactCalloutProps>(
  (
    { affectedCount, primaryDelta, secondaryDelta, complianceFlag, icon, className, ...rest },
    ref,
  ) => {
    // Compose full-sentence aria-label so screen readers announce a single
    // coherent phrase rather than fragmenting on punctuation.
    const ariaLabelParts: string[] = [affectedCount, primaryDelta];
    if (secondaryDelta !== undefined) ariaLabelParts.push(secondaryDelta);
    if (complianceFlag !== undefined) ariaLabelParts.push(complianceFlag.text);
    const ariaLabel = ariaLabelParts.join(", ");

    return (
      <div
        ref={ref}
        data-component="impact-callout"
        aria-label={ariaLabel}
        className={cx("nai-impact-callout", className)}
        {...rest}
      >
        {icon !== undefined && (
          <span aria-hidden="true" data-component="impact-callout-icon" className="nai-impact-callout-icon">
            {icon}
          </span>
        )}
        <span data-component="impact-callout-affected-count" className="nai-impact-callout-affected-count">
          {affectedCount}
        </span>
        <span aria-hidden="true" className="nai-impact-callout-separator">
          {" \u00b7 "}
        </span>
        <span data-component="impact-callout-primary-delta" className="nai-impact-callout-primary-delta">
          {primaryDelta}
        </span>
        {secondaryDelta !== undefined && (
          <>
            <span aria-hidden="true" className="nai-impact-callout-separator">
              {" \u00b7 "}
            </span>
            <span
              data-component="impact-callout-secondary-delta"
              className="nai-impact-callout-secondary-delta"
            >
              {secondaryDelta}
            </span>
          </>
        )}
        {complianceFlag !== undefined && (
          <>
            <span aria-hidden="true" className="nai-impact-callout-separator">
              {" \u00b7 "}
            </span>
            {complianceFlag.policyHref !== undefined ? (
              <a
                href={complianceFlag.policyHref}
                data-component="impact-callout-compliance-flag"
                className="nai-impact-callout-compliance-flag"
              >
                {complianceFlag.text}
              </a>
            ) : (
              <span
                data-component="impact-callout-compliance-flag"
                className="nai-impact-callout-compliance-flag"
              >
                {complianceFlag.text}
              </span>
            )}
          </>
        )}
      </div>
    );
  },
);
ImpactCallout.displayName = "ImpactCallout";
